import {useAppSelector} from "@Hooks/selector/useAppSelector";
import {useEffect, useState} from "react";
import useCompanyStyle from "@Hooks/selector/useCompanyStyle";
import {PagesType} from "@/types/pageSlice/Pages.type";
import UrlObjectType from "@/types/pageSlice/UrlObject.type";
import useDebouncedEventListener from "@Hooks/eventListeners/useDebouncedEventListener";
import {CompanySpecificNavigationType} from "@/types/pageSlice/CompanySpecificNavigation.type";

/**
 * @param topBarRef ref of the main menu wrapper, so we know on the resize when to hide the last element
 * @param menuItems array of menu items, manipulate them before sending them here so we can split them universally
 * @returns {object} object with the visible menu list, more menu list, show dropdown state, set dropdown function, toggle dropdown function and handle resize function
 * @description Hook that handles the entire main menu, from the dropdown to the resizing of the menu.
 */
export const useHandleMainMenu = (topBarRef: React.RefObject<HTMLDivElement>, menuItems: [PagesType, UrlObjectType][]) => {
    const [showDropdown, setDropdown] = useState(false);
    const [visibleMenuList, setVisibleMenuList] = useState<[PagesType, UrlObjectType][] | CompanySpecificNavigationType[]>(menuItems);
    const [moreMenuList, setMoreMenuList] = useState<[PagesType, UrlObjectType][] | CompanySpecificNavigationType[]>([]);

    const toggleDropdown = () => () => setDropdown(!showDropdown);

    let hideElement = false;

    // Once the page is resized or rendered, we create the menu items
    // and split them into two arrays, one for the visible menu and one for the more dropdown
    // this is done by checking the height of the top bar and if it is greater than 70px
    // if so we hide the last element of the visible menu and add it to the more menu
    const handleResize = (changedLayout = false) => {
        if (topBarRef?.current && (topBarRef.current.scrollHeight > 70 && topBarRef.current.scrollHeight < 141) && !hideElement) {
            hideElement = true;
            if (visibleMenuList.length > 0) {
                const updatedVisibleMenuList = visibleMenuList.slice(0, -1);
                const lastItem = visibleMenuList[visibleMenuList.length - 1];
                setVisibleMenuList(updatedVisibleMenuList);
                setMoreMenuList([...moreMenuList, lastItem]);
            }
        } else if (moreMenuList.length > 0 && changedLayout) {
            const updatedMoreMenuList = moreMenuList.slice(0, -1);
            const lastItem = moreMenuList[moreMenuList.length - 1];
            setMoreMenuList(updatedMoreMenuList);
            setVisibleMenuList([...visibleMenuList, lastItem]);
        }
    };

    useDebouncedEventListener('resize', () => {
        hideElement = false;
        handleResize(true);
    })

    useEffect(() => {
        handleResize()
    }, [visibleMenuList, topBarRef.current?.scrollHeight]);

    return {
        visibleMenuList,
        moreMenuList,
        showDropdown,
        setDropdown,
        toggleDropdown,
        handleResize,
    }
}